// A DM's rulings at the table (issue 1136): "Flanking gives +4 here" applies to the next roll. The DM adds, keeps and
// removes them in Properties > Scene, beside the Combat rules setting (the table's other rules control); a player reads
// the list, read-only, at the top of Properties. A ruling is a bonus (to attacks, AC or saves; always, or ticked on the
// attack: `ChosenRulings`) or a rule change, made from a few common fields — the critical multiplier, the threat range,
// the highest and lowest natural results — or, behind "Advanced", an overlay in the homebrew layer's shape. The server
// checks a rule change against the rules the table plays by and refuses one that would break them, in words.
import { useContext, useState } from 'react';
import { MODIFIER_TO, type ModifierTo, type PlayerRuling, type Ruling, type RulingModifier, RULING_LIMITS } from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { useMayAct } from '../../table/mayAct';
import { ShellContext } from '../context';

const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs text-stone-100';
const btn = 'rounded bg-stone-800 px-2 py-0.5 text-xs text-stone-200 hover:bg-stone-700';
const TO_WORDS: Record<ModifierTo, string> = { attack: 'attacks', ac: 'AC', save: 'saves' };

/** "+4 to attacks, ticked on the attack": a bonus in words. */
export const modifierWords = (m: RulingModifier): string =>
  `${m.value > 0 ? '+' : '−'}${Math.abs(m.value)} ${m.label} to ${TO_WORDS[m.to]}${m.when === 'chosen' ? ', ticked on the attack' : ''}`;

/** The rulings that lapse when the session ends: every one not kept. */
export const lapsing = (rulings: Array<Ruling | PlayerRuling> | undefined): Array<Ruling | PlayerRuling> => (rulings ?? []).filter((r) => !r.keep);

/**
 * The `chosen` rulings to this roll, as tick boxes (the attack form). Nothing when there are none. The ticked ids travel
 * on the request; the server checks each is in force.
 */
export function ChosenRulings({ to, chosen, onChange }: { to: ModifierTo; chosen: string[]; onChange: (ids: string[]) => void }) {
  const rulings = useTableState().rulings ?? [];
  const offered = rulings.filter((r): r is (Ruling | PlayerRuling) & { modifier: RulingModifier } => 'modifier' in r && !!r.modifier && r.modifier.when === 'chosen' && r.modifier.to === to);
  if (!offered.length) return null;
  return (
    <fieldset className="flex flex-wrap items-center gap-2 text-xs" data-testid="chosen-rulings">
      <legend className="sr-only">Rulings that apply</legend>
      <span className="text-stone-400">Rulings:</span>
      {offered.map((r) => (
        <label key={r.id} className="flex items-center gap-1" title={r.text}>
          <input type="checkbox" aria-label={`${r.modifier.value > 0 ? '+' : '−'}${Math.abs(r.modifier.value)} ${r.modifier.label}`} checked={chosen.includes(r.id)}
            onChange={(e) => onChange(e.target.checked ? [...chosen, r.id] : chosen.filter((x) => x !== r.id))} />
          {`${r.modifier.value > 0 ? '+' : '−'}${Math.abs(r.modifier.value)} ${r.modifier.label}`}
        </label>
      ))}
    </fieldset>
  );
}

/** A uuid for a new ruling: the browser's, else one made from its random numbers. */
const newId = (): string => (typeof crypto.randomUUID === 'function' ? crypto.randomUUID()
  : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) => (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0]! & (15 >> (Number(c) / 4)))).toString(16)));

type Natural = 'same' | 'both' | 'none';

/** The rule change the common fields describe, or a reason it cannot be made. */
export function ruleFromFields(f: { multiplier: string; threat: string; natural: Natural }, dice: { count: number; sides: number } | null): { combat: unknown } | string {
  const attack: Record<string, unknown> = {};
  const critical: Record<string, unknown> = {};
  if (f.multiplier.trim()) {
    const n = Number(f.multiplier);
    if (!Number.isInteger(n) || n < 2 || n > 10) return 'A critical multiplier is a whole number from 2 to 10.';
    critical.multiplier = n;
  }
  if (f.threat.trim()) {
    const n = Number(f.threat);
    if (!Number.isInteger(n)) return 'A threat range starts at a whole number.';
    critical.threat = n;
  }
  if (Object.keys(critical).length) attack.critical = critical;
  if (f.natural !== 'same') {
    if (!dice) return 'This table has no attack dice to name a natural result of.';
    attack.natural = f.natural === 'none' ? [] : [{ face: dice.count * dice.sides, result: 'hit' }, { face: dice.count, result: 'miss' }];
  }
  if (!Object.keys(attack).length) return 'Change at least one rule.';
  return { combat: { attack } };
}

/** The DM's add form. */
function AddRuling() {
  const { act, setNotice, systemRules } = useTableState();
  const [text, setText] = useState('');
  const [kind, setKind] = useState<'modifier' | 'rule'>('modifier');
  const [label, setLabel] = useState('');
  const [to, setTo] = useState<ModifierTo>('attack');
  const [value, setValue] = useState('');
  const [when, setWhen] = useState<'always' | 'chosen'>('chosen');
  const [multiplier, setMultiplier] = useState('');
  const [threat, setThreat] = useState('');
  const [natural, setNatural] = useState<Natural>('same');
  const [advanced, setAdvanced] = useState(false);
  const [raw, setRaw] = useState('');
  const [keep, setKeep] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dice = systemRules.attack?.dice ?? null;
  const highest = dice ? dice.count * dice.sides : null;

  const add = () => {
    setError(null);
    const words = text.trim();
    if (!words) return setError('Say the ruling in words the players will read.');
    let what: { modifier: RulingModifier } | { rule: unknown };
    if (kind === 'modifier') {
      const n = Number(value);
      if (!label.trim()) return setError('Name the bonus ("Flanking").');
      if (!Number.isInteger(n) || n === 0 || Math.abs(n) > RULING_LIMITS.value) return setError(`A bonus is a whole number from −${RULING_LIMITS.value} to ${RULING_LIMITS.value}, not 0.`);
      what = { modifier: { label: label.trim(), to, value: n, when } };
    } else if (advanced && raw.trim()) {
      try { what = { rule: JSON.parse(raw) as unknown }; } catch { return setError('The rule change is not valid JSON.'); }
    } else {
      const rule = ruleFromFields({ multiplier, threat, natural }, dice);
      if (typeof rule === 'string') return setError(rule);
      what = { rule };
    }
    const ruling = { id: newId(), text: words, keep, at: new Date().toISOString(), ...what };
    act({ kind: 'ruling.add', ruling } as never, (reply) => {
      if (!reply.ok) { setError(reply.reason ?? 'That ruling could not be made.'); setNotice(reply.reason ?? 'That ruling could not be made.'); return; }
      setText(''); setLabel(''); setValue(''); setMultiplier(''); setThreat(''); setNatural('same'); setRaw(''); setKeep(false);
    });
    return undefined;
  };

  return (
    <div className="mt-1 space-y-1 rounded border border-stone-800 p-1.5" data-testid="add-ruling">
      <label className="flex items-center gap-1 text-xs">
        <span className="text-stone-400">Ruling:</span>
        <input aria-label="The ruling, as players read it" className={`${field} flex-1`} maxLength={RULING_LIMITS.text} placeholder="Flanking gives +4 here" value={text} onChange={(e) => setText(e.target.value)} />
      </label>
      <label className="flex items-center gap-1 text-xs">
        <span className="text-stone-400">It is</span>
        <select aria-label="Kind of ruling" className={field} value={kind} onChange={(e) => setKind(e.target.value as 'modifier' | 'rule')}>
          <option value="modifier">a bonus</option>
          <option value="rule">a rule change</option>
        </select>
      </label>
      {kind === 'modifier' ? (
        <div className="flex flex-wrap items-center gap-1 text-xs">
          <input aria-label="Bonus name" className={`${field} w-24`} maxLength={RULING_LIMITS.label} placeholder="Flanking" value={label} onChange={(e) => setLabel(e.target.value)} />
          <input aria-label="Bonus" className={`${field} w-12`} inputMode="numeric" placeholder="+4" value={value} onChange={(e) => setValue(e.target.value.replace(/^\+/, ''))} />
          <span className="text-stone-400">to</span>
          <select aria-label="Bonus to" className={field} value={to} onChange={(e) => setTo(e.target.value as ModifierTo)}>
            {MODIFIER_TO.map((k) => <option key={k} value={k}>{TO_WORDS[k]}</option>)}
          </select>
          <select aria-label="When it applies" className={field} value={when} onChange={(e) => setWhen(e.target.value as 'always' | 'chosen')}>
            <option value="chosen">ticked on the attack</option>
            <option value="always">always</option>
          </select>
        </div>
      ) : (
        <div className="space-y-1 text-xs">
          <div className="flex flex-wrap items-center gap-1">
            <label className="flex items-center gap-1"><span className="text-stone-400">Critical ×</span>
              <input aria-label="Critical multiplier" className={`${field} w-10`} inputMode="numeric" placeholder={String(systemRules.attack?.critical?.multiplier ?? '')} value={multiplier} onChange={(e) => setMultiplier(e.target.value)} />
            </label>
            <label className="flex items-center gap-1"><span className="text-stone-400">threat from</span>
              <input aria-label="Threat range starts at" className={`${field} w-10`} inputMode="numeric" placeholder={String(systemRules.attack?.critical?.threat ?? '')} value={threat} onChange={(e) => setThreat(e.target.value)} />
            </label>
            {highest !== null && (
              <select aria-label="Natural results" className={field} value={natural} onChange={(e) => setNatural(e.target.value as Natural)}>
                <option value="same">natural results unchanged</option>
                <option value="both">{`natural ${highest} hits, ${dice!.count} misses`}</option>
                <option value="none">no automatic hit or miss</option>
              </select>
            )}
          </div>
          <label className="flex items-center gap-1 text-stone-400"><input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} /> Advanced</label>
          {advanced && (
            <textarea aria-label="Rule change (JSON)" className={`${field} h-16 w-full font-mono`} placeholder={'{ "combat": { "attack": { "critical": { "multiplier": 3 } } } }'} value={raw} onChange={(e) => setRaw(e.target.value)} />
          )}
        </div>
      )}
      <div className="flex items-center gap-2 text-xs">
        <label className="flex items-center gap-1"><input type="checkbox" aria-label="Keep for later sessions" checked={keep} onChange={(e) => setKeep(e.target.checked)} /> keep for later sessions</label>
        <button type="button" className={btn} onClick={add}>Add ruling</button>
      </div>
      {error && <p role="alert" className="text-xs text-amber-300">{error}</p>}
    </div>
  );
}

/** The campaign's own rule options that are on (qa-runs 1169), read-only for everyone: the DM sets them on the site. */
function CampaignOptions() {
  const options = useContext(ShellContext)?.table.ruleOptions ?? [];
  if (!options.length) return null;
  return (
    <section className="text-xs" data-testid="campaign-rule-options" aria-label="Campaign rules">
      <h3 className="vtt-section-head">Campaign rules</h3>
      <ul className="mt-1 space-y-0.5">{options.map((o) => <li key={o}>{o}</li>)}</ul>
    </section>
  );
}

/** The DM's rulings, editable; a player's, read-only ("Rulings in force: …"), each under the campaign's own options. Nothing for a player when there are none. */
export function RulingsSection() {
  return <><CampaignOptions /><Rulings /></>;
}

function Rulings() {
  const { act, setNotice } = useTableState();
  const mayAct = useMayAct();
  // `?? []`: a table state from before issue 1136 (a test's stand-in) carries none.
  const rulings = useTableState().rulings ?? [];
  const [adding, setAdding] = useState(false);
  // `dice`'s (issues 1159, 1160): a co-DM or guest DM without it reads the rulings as a player does.
  const isDm = mayAct('ruling.add');
  if (!isDm) {
    if (!rulings.length) return null;
    return (
      <section className="text-xs" data-testid="rulings-in-force" aria-label="Rulings in force">
        <h3 className="vtt-section-head">Rulings in force</h3>
        <ul className="mt-1 space-y-0.5">
          {rulings.map((r) => <li key={r.id}>{r.text}{r.keep ? <span className="text-stone-500"> (kept)</span> : null}</li>)}
        </ul>
      </section>
    );
  }
  const send = (body: unknown) => act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be changed.'); });
  return (
    <div className="text-xs" data-testid="rulings">
      <div className="flex items-center gap-2">
        <span className="text-stone-400" title="A DM's ruling applies to the next roll. It lasts the session unless kept; players see the list.">Rulings:</span>
        {!rulings.length && <span className="text-stone-500">none</span>}
        <button type="button" className={btn} onClick={() => setAdding((a) => !a)}>{adding ? 'Close' : 'Add a ruling…'}</button>
      </div>
      {rulings.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {rulings.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2">
              <span>{r.text}</span>
              {'modifier' in r && r.modifier && <span className="text-stone-500">{`(${modifierWords(r.modifier)})`}</span>}
              {'rule' in r && <span className="text-stone-500">(a rule change)</span>}
              <label className="flex items-center gap-1 text-stone-400">
                <input type="checkbox" aria-label={`Keep "${r.text}" for later sessions`} checked={r.keep} onChange={(e) => send({ kind: 'ruling.keep', id: r.id, keep: e.target.checked })} /> keep
              </label>
              <button type="button" className={btn} aria-label={`Remove "${r.text}"`} onClick={() => send({ kind: 'ruling.remove', id: r.id })}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      {adding && <AddRuling />}
    </div>
  );
}
